import {Button, DatePicker, Input, InputNumber, Modal, Popover, Radio, Select, Space, Spin, Steps} from 'antd'
import {course_getCourses_Api, course_promotePreview_Api, curriculum_getByOwner_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createSelectValue, createValue} from 'components/form/utils'
import {Formik, useFormikContext} from 'formik'
import {getDateString, parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {compose, mapProps, pure} from 'recompose'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import {
    course_delivery_mode_options,
    course_fee_currency_options,
    course_fee_mode_options,
    course_fee_structure_type_options,
    course_status_options
} from '..'
import DynamicFieldsDeclaration from '../../../../components/DynamicFields/DynamicFieldDeclaration'
import FormActionBar from '../../../../components/form/FormActionBar'
import SlateEditor from '../../../SlateEditor'
import {MedalSingleSelect} from '../../../Workspace/Medal/MedalSelect'
import {FeeTypesEditor} from '../../../Workspace/Course/components/FeeTypesEditor'
import {bindQueryParams} from '../../functions/routerHelper'
import {appClassNames} from '../../custom/appClassNames'
import {createControlledFormField, createNumberControlledInput,} from '../../factory/createFormField'
import {notifyOnError} from '../../factory/createErrorEvent'
import {PiChalkboardSimpleBold} from 'react-icons/pi'
import {GoDotFill} from 'react-icons/go'
import {renderIfElse} from '../../../Shared'
import {LuCornerLeftDown, LuCornerRightDown} from 'react-icons/lu'
import {TbSum, TbTimelineEventText} from 'react-icons/tb'
import TextArea from 'antd/lib/input/TextArea'
import {CustomToggleField} from '../../../../components/form/fields/ToggleField'
import Pure from '../../../../components/Pure'
import Translate from '../../../../modules/local/Translate'
import {ClassroomSvg} from '../../../../components/Svgs/ClassroomSvg'
import {BsArrowRight, BsInfoCircle} from 'react-icons/bs'
import LookupField from '../../../../components/LookupField'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import dayjs from 'dayjs'
import {ActionModalWrapper} from '../../../../components/form/ActionModalWrapper'
import {ErrorSummaries} from '../../factory/createEditableForm'
import {ROOT_URL} from '../../../../envs/_current/config'
import TooltipApp from '../../../../components/TooltipApp'
import {
    dateAfter,
    lengthBetween,
    noDoubleSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules,
    touchFields,
} from '../../../../components/form/ValidateForm'
import {CategoryField} from '../../../../components/form/fields/CategoryField'
import CourseCoverField from './components/CourseCoverField'
import {privacyOptions} from '../../../../conts/privacyOptions'
import {ChipGroup, Field, Section} from "../../../../components/form/ModernForm";
import {SyncFormikSubmitting} from "../../../../components/form/SyncFormikSubmitting";
import LinkedPages from "../../../Organization/Settings/Profile/LinkedPages";

// ─── Curriculum selector ──────────────────────────────────────────────────────
// Must be defined before formSchema (const TDZ — used as a component reference).

// Dropdown chọn chương trình đào tạo của owner - phần fetch + render dùng CHUNG cho cả form Tạo
// (CurriculumSelectField bên dưới, có confirm "apply curriculum"/xoá about khi đổi) và form Sửa
// (CurriculumField, định nghĩa cùng CurriculumReadonlyBadge cũ - chỉ set thẳng curriculum_id lúc
// CHƯA gán, không cascade gì, xem comment ở đó) - tránh lặp lại logic fetch/render option 2 nơi.
// Thiếu owner (vd form Sửa mở từ tab "Cấu hình Khóa học" không truyền owner_id/owner_type) thì
// KHÔNG được gọi curriculum_getByOwner_Api: path :type/:id không có giá trị để thay -> BE nhận
// literal "/courses/curriculum/:type/:id/gets" và trả 500, useAsync tự retry gây gọi API hàng loạt.
// Tách phần fetch ra component riêng, chỉ mount khi đã đủ owner (hook không gọi có điều kiện được).
const CurriculaSelect = ({owner_id, owner_type, value, onChange}) => {
    if (!owner_id || !owner_type) {
        return <CurriculaSelectView loading={false} curricula={[]} value={value} onChange={onChange}/>
    }
    return <CurriculaSelectWithData {...{owner_id, owner_type, value, onChange}}/>
}

const CurriculaSelectWithData = ({owner_id, owner_type, value, onChange}) => {
    const {response, isLoading} = useAsync({
        apiInfo: curriculum_getByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })

    const curricula = _.get(response, 'data.data', [])

    return (
        <CurriculaSelectView
            loading={isLoading}
            curricula={curricula}
            value={value}
            onChange={onChange}
        />
    )
}

const CurriculaSelectView = ({loading, curricula, value, onChange}) => {
    const t = useTranslate()

    return (
        <SelectWidget
            loading={loading}
            className="w-full"
            placeholder={t('select curriculum')}
            value={value || undefined}
            allowClear
            showSearch
            optionLabelProp="label"
            filterOption={(input, option) =>
                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(newId) => {
                const curriculum = curricula.find((c) => c.id === newId) ?? null
                onChange(newId ?? null, curriculum)
            }}>
            {curricula.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.title} searchText={c.title}>
                    <div className="flex flex-col gap-0.5 py-0.5">
                        <span className="font-bold text-sm text-gray-700">
                            {c.title}
                        </span>
                        <div className="flex items-center gap-2 tracking-wider">
                            <span className="text-color-200">
                                <GoDotFill size={10} className="mr-1 text-color-500"/>{c.subject_count} {t('subjects')}
                            </span>
                            {c.duration_months > 0 && (
                                <span className="text-color-200">
                                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                                    {c.duration_months} {t('months')}
                                </span>
                            )}
                        </div>
                        {c.description && (
                            <span className="mt-1 text-xs text-color-400 tracking-wider truncate">{c.description}</span>
                        )}
                    </div>
                </Select.Option>
            ))}
        </SelectWidget>
    )
}

// Lấy toàn bộ subject_id (id của Subject gốc, KHÔNG phải id của CurriculumSubject) từ 1 curriculum -
// dùng làm giá trị mặc định "chọn hết" cho subject_ids mỗi khi đổi chương trình đào tạo, và cũng là
// danh sách gửi lên BE lúc submit (CourseController::createCourse lọc theo đúng subject_id này).
const getAllCurriculumSubjectIds = (curriculum) =>
    (curriculum?.subjects ?? []).map((s) => s?.subject?.id).filter(Boolean)

const CurriculumSelectField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const owner_id = _.get(form, 'values.owner_id')
    const owner_type = _.get(form, 'values.owner_type')

    const handleChange = useCallback((newId, curriculum) => {
        const hasAbout = !!_.get(form, 'values.about')
        const hasCurriculum = !!_.get(form, 'values.curriculum_id')

        if (newId && hasAbout) {
            Modal.confirm({
                title: t('apply curriculum'),
                content: t('apply curriculum confirm content'),
                okText: t('confirm'),
                cancelText: t('cancel'),
                onOk: () => {
                    form.setFieldValue(name, newId)
                    form.setFieldValue('_curriculum', curriculum)
                    form.setFieldValue('subject_ids', getAllCurriculumSubjectIds(curriculum))
                    form.setFieldValue('about', undefined)
                },
            })
        } else if (!newId && hasCurriculum) {
            Modal.confirm({
                title: t('confirm'),
                content: t('remove curriculum confirm content'),
                okText: t('confirm'),
                cancelText: t('cancel'),
                onOk: () => {
                    form.setFieldValue(name, null)
                    form.setFieldValue('_curriculum', null)
                    form.setFieldValue('subject_ids', undefined)
                },
            })
        } else {
            form.setFieldValue(name, newId ?? null)
            form.setFieldValue('_curriculum', curriculum)
            // Chọn chương trình mới (chưa có "about" để hỏi ghi đè) - mặc định SELECT HẾT môn của
            // chương trình đó luôn, người tạo bỏ bớt tay ở CurriculumPreview bên dưới nếu cần.
            form.setFieldValue('subject_ids', newId ? getAllCurriculumSubjectIds(curriculum) : undefined)
        }
    }, [form, name, t])

    return (
        <CurriculaSelect
            owner_id={owner_id}
            owner_type={owner_type}
            value={value}
            onChange={handleChange}
        />
    )
}

const contentOptionsCopy = [
    {
        label: 'classes',
        value: 'classes',
    },
    {
        label: 'course committee',
        value: 'course_teachers',
    },
    {
        label: 'subjects',
        value: 'subjects',
    },
    {
        label: 'students',
        value: 'students',
        disabled: true,
    },
]

export const formSchema = {
    status: {
        // title: 'status',
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="custom-radio-button"
                        />
                    )
                },
            },
        ],
    },
    academic_year: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                name: 'academic_year',
                label: 'academic year',
                component: createNumberControlledInput({
                    placeholder: 'academic year',
                    type: 'number',
                    formatter: (value) =>
                        `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
                    parser: (value) => {
                        const parsed = value.replace(/\$\s?|(,*)/g, '')
                        return parsed ? Number(parsed) : 0 // ✅ convert sang number
                    },
                }),
            },
        ],
    },
    academic_field: {
        // title: 'academic field',
        children: [
            {
                name: 'academic_field',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    const owner = _.get(values, 'owner', {})
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.academic_field}
                                    apiPath={`/lookup/${getType(owner)}/${getId(owner)}/field/academic_field/gets`}
                                    name={name}
                                    placeholder={t('choose or create title')}
                                    handleChange={(e) => {
                                        onChange(createValue('academic_field', e.target.value))
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    title: {
        // title: 'title of course',
        isRequired: true,
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'title of course',
                    showCount: true,
                    maxLength: 180,
                    className: 'rounded-lg',
                }),
            },
        ],
    },
    description: {
        // title: 'short description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 5,
                    showCount: true,
                    maxLength: 500,
                    placeholder: 'short description',
                    Component: TextArea,
                    className: 'scrollBarThin',
                }),
            },
        ],
    },
    idname: {
        // title: 'url',
        children: {
            name: 'idname',
            component: compose(
                withTranslate,
                mapProps(({onChange, value, translate}) => ({
                    onChange,
                    value,
                    name: 'idname',
                    placeholder: translate('please input name!'),
                    type: 'text',
                    trigger: ['focus'],
                    title: ROOT_URL + '/course/' + value,
                    placement: 'topLeft',
                })),
                pure
            )(({title, trigger, placement, ...inputProps}) => (
                <TooltipApp title={title} trigger={trigger} placement={placement}>
                    <Space.Compact className="w-full">
                        <Input value="https://.../course/" disabled style={{width: 'auto'}}/>
                        <Input {...inputProps} />
                    </Space.Compact>
                </TooltipApp>
            )),
        },
    },
    // Mã khóa — công khai trên card /edu (renderCourseSearchViewItem.js). Cho phép sửa, tự
    // UPPERCASE + chỉ giữ [A-Z0-9-] khi gõ (không cho gõ ký tự khác luôn, không đợi validate),
    // uniqueness check TOÀN HỆ THỐNG nằm ở CourseController@createCourse/editCourse (BE) - FE chỉ
    // lọc ký tự, không tự đoán trùng.
    course_idcode: {
        children: [
            {
                name: 'course_idcode',
                component: createControlledFormField({
                    placeholder: 'ex: GXTT26-01GLY',
                    noTranslate: true,
                    maxLength: 30,
                    className: 'uppercase tracking-widest',
                    withProps: ({name, onChange = NullAsDefault}) => ({
                        onChange: (e) => {
                            const filtered = (e?.target?.value || '')
                                .toUpperCase()
                                .replace(/[^A-Z0-9-]/g, '')
                            onChange(createValue(name, filtered))
                        },
                    }),
                }),
            },
        ],
    },

    cover_photo: {
        children: [
            {
                name: 'cover_photo',
                component: ({name, value, form, onChange}) => (
                    <CourseCoverField
                        values={_.get(form, 'values')}
                        {...{name, value, onChange}}
                    />
                ),
            },
        ],
    },

    // "Thông tin dự kiến" — coordinator_name/location/schedule/delivery_mode/fee_amount, dùng khi
    // khóa CHƯA có course_class/timetable/teacher chính thức. Hiện thành 3 dòng info + badge hình
    // thức + badge phí trên card /edu (renderCourseSearchViewItem.js) - gộp 1 nhóm ở đây theo đúng
    // tương quan hiển thị.
    coordinator_name: {
        children: [
            {
                name: 'coordinator_name',
                component: createControlledFormField({
                    placeholder: 'ex: coordinator name',
                }),
            },
        ],
    },
    location: {
        children: [
            {
                name: 'location',
                component: createControlledFormField({
                    placeholder: 'ex: course location',
                }),
            },
        ],
    },
    schedule: {
        children: [
            {
                name: 'schedule',
                component: createControlledFormField({
                    placeholder: 'ex: course schedule',
                }),
            },
        ],
    },
    // fee_note (text tự do) không còn hiện ở form nữa - thay bằng fee_amount (số) +
    // fee_currency_code (VND/USD), xem database/sql/087_add_delivery_mode_and_fee_fields_to_courses.sql.
    // Dữ liệu fee_note cũ (nếu có) vẫn còn ở DB nhưng không đọc/ghi lại từ đây nữa.
    delivery_mode: {
        children: {
            name: 'delivery_mode',
            component: compose(withTranslate)(({name, translate: t, onChange, value}) => (
                <SelectWidget
                    name={name}
                    className="w-full"
                    // Cột DB NOT NULL (mặc định 1/offline) - KHÔNG cho allowClear, tránh gửi
                    // null/undefined lên BE (sẽ bị chặn ở validation, nhưng để tránh cả trường hợp
                    // đó luôn - Select chỉ cho đổi giữa 3 giá trị hợp lệ, không xoá được về trống).
                    value={value || undefined}
                    onChange={(v) => onChange(createSelectValue(name, v))}
                    placeholder={t('---Choose One---')}
                    options={Object.values(course_delivery_mode_options).map(deepTranslate(t))}
                />
            )),
        },
    },
    // Trạng thái phí (free/paid/donation) - cột riêng fee_mode, KHÔNG còn suy từ fee_amount===0
    // (không phân biệt được "miễn phí" và "tùy tâm đóng góp"), xem
    // database/sql/096_add_fee_mode_to_courses.sql. Chọn "có phí" mới hiện input số tiền + loại
    // tiền bên dưới (xem FeeAmountCurrencyField).
    fee_mode: {
        children: {
            name: 'fee_mode',
            component: compose(withTranslate)(({name, translate: t, onChange, value}) => (
                <SelectWidget
                    name={name}
                    className="w-full"
                    // Cột DB NOT NULL (mặc định 1/free) - không cho allowClear, tương tự delivery_mode.
                    value={value || undefined}
                    onChange={(v) => onChange(createSelectValue(name, v))}
                    placeholder={t('---Choose One---')}
                    options={Object.values(course_fee_mode_options).map(deepTranslate(t))}
                />
            )),
        },
    },
    // Cách tính khi fee_mode=paid - fixed (mặc định, hành vi cũ) dùng fee_amount cố định bên dưới;
    // itemized bỏ qua fee_amount, khai chi tiết định mức/tín chỉ ở tab "Tài Chính & Học Phí" (chỉ
    // hiện khi khóa ĐANG MỞ - đổi qua lại lúc đang có học viên vẫn an toàn, BE tự tính lại toàn bộ
    // học viên khi chuyển sang itemized, xem CourseFeeTypeObserver/Course::edit()).
    fee_structure_type: {
        children: {
            name: 'fee_structure_type',
            component: compose(withTranslate)(({name, translate: t, onChange, value}) => (
                <SelectWidget
                    name={name}
                    className="w-full"
                    value={value || course_fee_structure_type_options.fixed.value}
                    onChange={(v) => onChange(createSelectValue(name, v))}
                    options={Object.values(course_fee_structure_type_options).map(deepTranslate(t))}
                />
            )),
        },
    },
    fee_amount_currency: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                name: 'fee_amount',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange,
                        prefix: (
                            <span className="mr-0.5 font-medium text-xs text-color-400 tracking-wide">
                                {`${translate('fee amount')}: `}
                            </span>
                        ),
                        placeholder: '0',
                        type: 'number',
                        min: 0,
                        formatter: (value) => `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ','),
                        parser: (value) => {
                            const parsed = value.replace(/[^0-9]/g, '')
                            return parsed ? Number(parsed) : undefined
                        },
                    }))
                )(Input),
            },
            {
                name: 'fee_currency_code',
                component: compose(withTranslate)(({name, translate: t, onChange, value}) => (
                    <SelectWidget
                        name={name}
                        className="w-full"
                        value={value || 'VND'}
                        onChange={(v) => onChange(createSelectValue(name, v))}
                        options={Object.values(course_fee_currency_options)}
                    />
                )),
            },
        ],
    },

    // "Điều kiện & Quy trình đăng ký" + "Bằng cấp & tiêu chuẩn tốt nghiệp" - màn Điều hành Khóa &
    // Lớp học (Workspace) hiện các mục này, text mô tả tự do admin tự nhập, không nhập thì màn đó
    // tự hiện empty-placeholder (xem database/sql/088_add_admission_and_graduation_fields_to_courses.sql).
    eligibility_requirements: {
        children: [
            {
                name: 'eligibility_requirements',
                component: createControlledFormField({
                    rows: 3,
                    showCount: true,
                    maxLength: 5000,
                    placeholder: 'ex: eligibility requirements placeholder',
                    Component: TextArea,
                    className: 'scrollBarThin rounded-lg',
                }),
            },
        ],
    },
    required_documents: {
        children: [
            {
                name: 'required_documents',
                component: createControlledFormField({
                    rows: 3,
                    showCount: true,
                    maxLength: 5000,
                    placeholder: 'ex: required documents placeholder',
                    Component: TextArea,
                    className: 'scrollBarThin  rounded-lg',
                }),
            },
        ],
    },
    graduation_criteria: {
        children: [
            {
                name: 'graduation_criteria',
                component: createControlledFormField({
                    rows: 3,
                    showCount: true,
                    maxLength: 5000,
                    placeholder: 'ex: graduation criteria placeholder',
                    Component: TextArea,
                    className: 'scrollBarThin  rounded-lg',
                }),
            },
        ],
    },

    privacy: {
        children: {
            name: 'privacy',
            component: compose(withTranslate)(({name, translate: t, onChange, value}) => {
                const desc = {
                    1: t('This content is available to everyone.'),
                    2: t('This content is available to members only.'),
                }
                return (
                    <div className="flex flex-col gap-1">
                        <SelectWidget
                            name={name}
                            className="w-full"
                            value={value || undefined}
                            onChange={(v) => onChange(createSelectValue('privacy', v))}
                            placeholder={t('---Choose One---')}
                            options={Object.values(privacyOptions).map(deepTranslate(t))}
                        />
                        {desc[value] && (
                            <p className="text-xs text-color-400 italic leading-relaxed tracking-wide">
                                {desc[value]}
                            </p>
                        )}
                    </div>
                )
            }),
        },
    },
    channel_post_permission: {
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate(
                        'Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post'
                    ),
                }))
            )(CustomToggleField),
        },
    },
    copy_course_teachers: {
        children: {
            name: 'copy_course_teachers',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Boolean(value),
                    onChange: (checked) => onChange(createValue(name, Boolean(checked))),
                    title: translate('course committee'),
                    description: translate(
                        'copy course committee description'
                    ),
                }))
            )(CustomToggleField),
        },
    },
    about: {
        // title: 'about course',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
    registration_period_quota: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                name: 'last_enrollment_time',
                label: 'last enrollment time',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        showTime: true,
                        className: 'w-full',
                        format: 'HH:mm - DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 07:00 - 20/10/2010'),
                        // Chỉ cho chọn trong khoảng 100 năm
                        disabledDate: (current) => {
                            const minDate = dayjs().subtract(100, 'year').startOf('day')
                            const maxDate = dayjs().add(100, 'year').endOf('day')
                            return current && (current < minDate || current > maxDate)
                        },
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
            {
                name: 'student_quota',
                label: 'student quota',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange,
                        className: 'w-full',
                        prefix: (
                            <span className="mr-0.5 font-medium text-xs text-color-400 tracking-wide">
                                {`${translate('student quota')}: `}
                            </span>
                        ),
                        placeholder: '0',
                        min: 0,
                        // formatter: (value) => `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ','),
                        // parser: (value) => {
                        //     const parsed = value.replace(/[^0-9]/g, '')
                        //     return parsed ? Number(parsed) : undefined
                        // },
                    }))
                )(InputNumber),
                // component: createNumberControlledInput({
                //
                //     placeholder: '0',
                //     type: 'number',
                //     min: 0,
                //     formatter: (value) =>
                //         `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ','),
                //     parser: (value) => {
                //         const parsed = value.replace(/[^0-9]/g, '')
                //         return parsed ? Number(parsed) : undefined
                //     },
                // }),
            },
        ],
    },
    started_finished_date: {
        inline: true,
        className: appClassNames.inline_3,
        children: [
            {
                name: 'time_open',
                isRequired: true,
                label: 'start date',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate, form}) => ({
                        name,
                        value,
                        // showTime: true,
                        className: 'w-full',
                        format: 'DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 20/10/2010'),
                        // Chỉ cho chọn trong khoảng 100 năm
                        disabledDate: (current) => {
                            const minDate = dayjs().subtract(100, 'year').startOf('day')
                            const maxDate = dayjs().add(100, 'year').endOf('day')
                            return current && (current < minDate || current > maxDate)
                        },
                        onChange: (date) => {
                            const nextDate = date ? dayjs(date) : null
                            form.setValues({
                                ...form.values,
                                time_open: nextDate,
                                academic_year: nextDate?.year()
                            }, true)
                            // Đổi ngày bắt đầu có thể làm ngày kết thúc ĐANG có sẵn trở nên không
                            // hợp lệ (bằng/trước ngày mới) - touch cả 2 field để lỗi hiện NGAY, không
                            // đợi submit mới thấy (xem touchFields, ValidateForm.js).
                            touchFields(form, 'time_open', 'time_close')
                        }
                    }))
                )(DatePicker),
            },
            {
                name: 'time_close',
                label: 'end date',
                isRequired: true,
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate, form}) => {
                        const timeOpen = form?.values?.time_open
                        return {
                            name,
                            value,
                            // showTime: true,
                            disabled: !timeOpen,
                            className: 'w-full',
                            format: 'DD/MM/YYYY',
                            suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                            placeholder: translate('ex: 20/10/2010'),
                            // Chỉ cho chọn trong khoảng 100 năm
                            disabledDate: (current) => {
                                const minDate = dayjs().subtract(100, 'year').startOf('day')
                                const maxDate = dayjs().add(100, 'year').endOf('day')
                                return current && (current < minDate || current > maxDate)
                            },
                            onChange: (date, dateString) => {
                                onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                                touchFields(form, 'time_close')
                            },
                        }
                    })
                )(DatePicker),
            },
            {
                name: 'academic_year',
                label: 'academic year',
                isRequired: true,
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate, form}) => ({
                        name,
                        value,
                        onChange,
                        className: 'w-full',
                        placeholder: dayjs().year(),
                        min: 2000,
                        max: 9999,
                        // formatter: (value) =>
                        //     `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
                        // parser: (value) => {
                        //     const parsed = value.replace(/\$\s?|(,*)/g, '')
                        //     return parsed ? Number(parsed) : undefined
                        // },
                        disabled: !form?.values?.time_open,
                    }))
                )(InputNumber),
                // component: createNumberControlledInput({
                //     prefix: (
                //         <span className="mr-2 text-color-400 italic">
                //                     {`${translate('start date')}: `}
                //                 </span>
                //     ),
                //     placeholder: 'academic year',
                //     type: 'number',
                //     min: 2000,
                //     max: 9999,
                //     formatter: (value) =>
                //         `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
                //     parser: (value) => {
                //         const parsed = value.replace(/\$\s?|(,*)/g, '')
                //         return parsed ? Number(parsed) : undefined
                //     },
                //     withForm: (form) => ({
                //         disabled: !form?.values?.time_open,
                //     }),
                // }),
            }
        ],
    },

    curriculum_id: {
        // title: 'training program',
        children: [
            {name: 'curriculum_id', component: CurriculumSelectField},
        ],
    },

    medal_id: {
        title: 'certification awarded',
        children: [
            {
                name: 'medal_id',
                // owner lấy từ values.owner (dữ liệu khóa học trả về sẵn từ course_getCourseById_Api -
                // xem CourseTransformer.php field 'owner') - CÙNG nguồn với academic_field ở trên, KHÔNG
                // qua OrganizationContainerContext nữa: context đó chỉ được cấp ở cây /workspace/...
                // (WorkspaceProvider.js bọc OrganizationContainerProvider), còn màn /access-portal
                // (AccessPortalPage.js) chỉ cấp WorkspaceContext tối giản - Consumer ở đây fallback về
                // default context rỗng nên owner luôn undefined, request get-medals ra
                // "/medals/undefined/undefined/get-medals" (lỗi user báo).
                component: ({name, form, value, onChange}) => {
                    const defaultValue = form?.values?.medal_id
                    const owner = _.get(form, 'values.owner', {})
                    return (
                        <Translate>
                            {(t) => (
                                <MedalSingleSelect
                                    {...{
                                        name,
                                        value,
                                        onChange,
                                        defaultValue,
                                    }}
                                    className="custom-select-medal"
                                    placeholder={t('click to select')}
                                    type="medal"
                                    owner_id={getId(owner)}
                                    owner_type={getType(owner)}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    metadata: {
        // title: 'registration questions',
        children: [
            {
                name: 'metadata',
                component: ({name, value, onChange, errors}) => {
                    return (
                        <DynamicFieldsDeclaration
                            name={name}
                            value={value}
                            className="w-full"
                            onChange={(value) => {
                                onChange(createValue(name, value))
                            }}
                            errors={errors?.metadata || {}}
                        />
                    )
                },
            },
        ],
    },

    categories: {
        // title: 'categories',
        children: [
            {
                name: 'categories',
                component: CategoryField,
            },
        ],
    },

    copy_content: {
        title: 'content to keep',
        children: [
            {
                name: 'copy_content',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange}) => ({
                        name,
                        value,
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                        multiple: true,
                        options: contentOptionsCopy,
                    }))
                )(ChipGroup),
            },
        ],
    },

    // Link ngoài dùng chung cho cả khóa (Zalo, nhóm Zalo, website riêng...) - hiển thị lại trên
    // trang lớp học (ClassroomHeader, CourseClassPage.js) gộp cùng link riêng của từng lớp. Tái
    // dùng THẲNG component LinkedPages đang dùng cho Organization (Organization/Settings/Profile/
    // LinkedPages.js) - cùng convention linked_pages (page/url), không tự chế UI song song.
    linked_pages: {
        title: 'external links',
        children: [
            {
                name: 'linked_pages',
                component: compose(
                    mapProps(({name, value, onChange}) => ({
                        data: value,
                        classNames: ['flex items-center gap-3'],
                        renderWrapper: ({children}) => (
                            <div className="flex flex-col gap-3">{children}</div>
                        ),
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                    })),
                    pure
                )(LinkedPages),
            },
        ],
    },
}

// ─── Fee amount/currency gate ─────────────────────────────────────────────────
// fee_amount/fee_currency_code (fixed) HOẶC bảng định mức course_fee_types (itemized) chỉ có ý
// nghĩa khi fee_mode=paid (2) - free/donation không có mức cố định nên ẩn hẳn, tránh để trống gây
// hiểu lầm "quên nhập số tiền" (xem database/sql/096_add_fee_mode_to_courses.sql, course_fee_mode_options).
//
// itemized: MỞ NGAY tại đây form khai định mức (FeeTypesEditor - dùng CHUNG với tab "Tài Chính &
// Học Phí", xem views/Workspace/Course/components/FeeTypesEditor.js) thay vì chỉ ghi chú trỏ sang
// tab khác - bạn yêu cầu cho nhập/sửa/xoá định mức NGAY ở đây, list hiện có cũng hiển thị lại luôn
// để admin thấy đang có những loại phí gì, chi tiết từng loại. Chỉ khai được khi khóa đã tồn tại
// (có course_id thật - course_fee_types là bảng con FK course_id, khóa đang tạo mới chưa có id).
const FeeAmountCurrencyField = () => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const course_id = getId(values)

    if (values.fee_mode !== course_fee_mode_options.paid.value) {
        return (
            <p className="text-xs text-color-400 italic tracking-wide">
                {values.fee_mode === course_fee_mode_options.donation.value
                    ? t('donation based fee hint')
                    : t('free fee hint')}
            </p>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <FieldsFactory formSchema={[formSchema.fee_structure_type]}/>
            {values.fee_structure_type === course_fee_structure_type_options.itemized.value ? (
                course_id ? (
                    <FeeTypesEditor course_id={course_id} can_edit={true}/>
                ) : (
                    <p className="text-xs text-amber-600 italic tracking-wide">
                        {t('save course before itemized fee hint')}
                    </p>
                )
            ) : (
                <FieldsFactory formSchema={[formSchema.fee_amount_currency]}/>
            )}
        </div>
    )
}

// ─── Curriculum preview (create form) ────────────────────────────────────────
// Trước là preview READ-ONLY. Nay cho chọn/bỏ chọn TỪNG môn của chương trình (mặc định chọn hết
// ngay lúc đổi curriculum - xem getAllCurriculumSubjectIds ở CurriculumSelectField) - subject_ids
// được chọn ở đây gửi thẳng lên BE lúc submit (CourseController::createCourse), quyết định đúng
// những môn nào được sao chép (clone) vào khóa mới, KHÔNG còn cứng "copy hết" như trước. Dùng lại
// ChipGroup (đã import sẵn ở trên, đang dùng cho copy_content) thay vì tự vẽ checkbox mới - đúng
// pattern chip cho lựa chọn trực quan.
const CurriculumPreview = () => {
    const t = useTranslate()
    const {values, setFieldValue} = useFormikContext()
    const curriculum = values._curriculum

    if (!curriculum) return null

    const subjects = curriculum.subjects ?? []
    const allIds = getAllCurriculumSubjectIds(curriculum)
    const selectedIds = values.subject_ids ?? []
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id))

    return (
        <div className="shadow-md rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-blue-400 bg-blue-100">
                <span className="font-medium text-xs text-blue-500 uppercase tracking-wide">
                    {t('curriculum')}
                </span>
                <span className="flex-1 font-semibold text-sm text-blue-700 truncate">
                    {curriculum.title}
                </span>
            </div>
            <div className="p-3 flex flex-col gap-3">
                <div className="flex items-center gap-2 shrink-0">
                <span className="font-medium text-xs text-color-200">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {curriculum.subject_count} {t('subjects')}
                </span>
                    {curriculum.duration_months > 0 && (
                        <span className="font-medium text-xs text-color-200">
                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                            {curriculum.duration_months} {t('months')}
                    </span>
                    )}
                </div>
                {curriculum.description && (
                    <p className="text-xs text-color-300 tracking-wider">
                        {curriculum.description}
                    </p>
                )}
                {subjects.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-xs text-gray-600 uppercase tracking-wide">
                                {t('subjects')} ({selectedIds.length}/{allIds.length})
                            </span>
                            <button
                                type="button"
                                onClick={() => setFieldValue('subject_ids', allSelected ? [] : allIds)}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                                {allSelected ? t('deselect all') : t('select all')}
                            </button>
                        </div>
                        <p className="text-2xs text-color-400 italic leading-relaxed">
                            {t('curriculum subjects preselected hint')}
                        </p>
                        <ChipGroup
                            multiple
                            options={subjects.map((s) => ({
                                label: s.subject?.title || s.title || '-',
                                value: s.subject?.id,
                                disabled: !s.subject?.id,
                            }))}
                            value={selectedIds}
                            onChange={(next) => setFieldValue('subject_ids', next)}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── Curriculum field (edit form) ──────────────────────────────────────────────
// curriculum_id CHỈ khoá lại (đọc-only) SAU KHI đã gán - khớp CourseController::editCourse ("curriculum_id
// is immutable after assignment", chặn đổi khi đã có để tránh lệch subjects/about/medal đã snapshot
// lúc tạo). Khi CHƯA gán, cho chọn ngay tại đây (trước đây return null - màn Cấu hình Khóa học
// không có cách nào gán chương trình sau khi khóa đã tạo, bạn góp ý) - khác CurriculumSelectField
// (form Tạo) ở chỗ KHÔNG confirm "apply curriculum"/xoá about: lúc Sửa, BE (editCourse) không chạy
// lại snapshot subjects/about/medal từ chương trình xuống khóa (logic đó chỉ có ở createCourse), nên
// chỉ cần set thẳng curriculum_id, không cần hỏi xác nhận ghi đè gì (bạn yêu cầu).
const CurriculumField = ({ownerId, ownerType}) => {
    const t = useTranslate()
    const {values, setFieldValue} = useFormikContext()

    console.log(999998888, values)

    if (values?.curriculum_id) {
        const curriculumTitle = values?.curriculum?.title
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-color-400 uppercase tracking-wide">
                    {t('training program')}
                </span>
                <div
                    className="flex items-center gap-2 rounded-md border border-color-50 background-100 px-3 py-2 cursor-not-allowed opacity-70">
                    {curriculumTitle ? (
                        <span className="flex-1 text-sm text-color-200">{curriculumTitle}</span>
                    ) : (
                        <span className="flex-1 text-sm text-color-400 italic">{t('none')}</span>
                    )}
                    <span className="text-xs text-color-500 italic shrink-0">{t('not editable')}</span>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-color-400 uppercase tracking-wide">
                {t('training program')}
            </span>
            <CurriculaSelect
                owner_id={ownerId ?? values?.owner_id}
                owner_type={ownerType ?? values?.owner_type}
                value={values?.curriculum_id}
                onChange={(newId, curriculum) => {
                    setFieldValue('curriculum_id', newId)
                    setFieldValue('curriculum', curriculum)
                }}
            />
            <p className="text-2xs text-color-400 italic leading-relaxed tracking-wide">
                {t('curriculum locked after selection hint')}
            </p>
        </div>
    )
}

const validateTitle = (name, t) => {
    // Bỏ noLeadingTrailingSpaces - validate live theo từng keystroke (validateOnChange) nên khoảng
    // trắng cuối chuỗi lúc đang gõ tiếp từ mới (rất bình thường) bị báo lỗi giả liên tục. Trim trước
    // khi validate + trim lại lần nữa lúc submit (xem onSubmit) là đủ, không cần chặn khi đang gõ.
    const trimmed = typeof name === 'string' ? name.trim() : name
    // Bỏ noSpecialChars - tựa đề khóa học thực tế cần được các ký tự thông thường như ngoặc đơn
    // "(HKI)", "(HKII)"... (học kỳ), dấu "/", ":" - regex cũ chỉ cho chữ/số/khoảng trắng/.'’- nên
    // chặn nhầm những tựa đề hợp lệ này. Không thay bằng regex khác - tựa đề là text tự do, đã có
    // required + giới hạn độ dài + chặn khoảng trắng đôi là đủ.
    const rules = [
        required(t),
        lengthBetween(2, 180, t),
        // noSpecialChars(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(trimmed, rules)
    if (message) return {title: message}
}

const validateDescription = (description, t) => {
    const rules = [lengthBetween(1, 500, t)].filter(Boolean)
    const message = runRules(description, rules)
    if (message) return {description: message}
}

const validateAcademicField = (name, t) => {
    // Cùng lý do bỏ noLeadingTrailingSpaces như validateTitle ở trên.
    const trimmed = typeof name === 'string' ? name.trim() : name
    const rules = [
        lengthBetween(2, 255, t),
        noNumber(t),
        noSpecialChars(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(trimmed, rules)
    if (message) return {academic_field: message}
}

const validateTimeOpen = (timeOpen, typeForm, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeOpen, rules)
    if (message) return {time_open: message}

}

const validateTimeClose = (timeOpen, timeClose, t) => {
    // dateAfter mặc định inclusive=false - ngày kết thúc BẰNG ngày bắt đầu cũng là lỗi (trước đây
    // chỉ check isBefore(), bỏ sót case "bằng").
    const rules = [required(t), timeOpen && dateAfter(timeOpen, t)].filter(Boolean)
    const message = runRules(timeClose, rules)
    if (message) return {time_close: message}
}

const validateAcademicYear = (value, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(value, rules)
    if (message) return {academic_year: message}
}

const validateLastEnrollmentTime = (timeOpen, last_enrollment_time, t) => {
    const error = {}
    if (!timeOpen || !last_enrollment_time) return error
    const start = dayjs(timeOpen).startOf('day')
    // const enrollmentDeadline = dayjs(last_enrollment_time).startOf('day')
    // if (enrollmentDeadline.isAfter(start.add(21, 'day'))) {
    //     return {
    //         last_enrollment_time: t('enrollment deadline cannot be more than 3 weeks after the course start date'),
    //     }
    // }

    return error
}

// Map field name -> i18n key nhãn hiển thị, truyền cho ErrorSummaries (createEditableForm.js) - mặc
// định ErrorSummaries hiện thẳng tên field kỹ thuật (vd "time_open") khi không tìm thấy key i18n
// trùng tên, KHÔNG map sẵn ở COMMON_FIELD_LABEL_KEYS dùng chung vì time_open/time_close ở module
// khác (vd CourseClassExamForm) label khác ("started at"/"finished at"), gán chung dễ sai lệch nơi
// khác - field riêng của Course thì khai báo riêng ở đây. `title` cố tình map qua key 'title of
// course' (= "Tên khóa học") thay vì để rơi về key 'title' mặc định (= "Tựa đề") - khớp ĐÚNG label
// đang hiển thị trên field thật (Section "TÊN KHÓA HỌC"), tránh 2 tên gọi khác nhau cho cùng 1 field.
const COURSE_ERROR_LABELS = {
    title: 'title of course',
    time_open: 'start date',
    time_close: 'end date',
    academic_year: 'academic year',
    academic_field: 'academic field',
    last_enrollment_time: 'last enrollment time',
    student_quota: 'student quota',
}

const validate = (t, typeForm) => (values) => {
    const {
        title,
        description,
        academic_field,
        time_open,
        time_close,
        academic_year,
        about,
        last_enrollment_time,
        metadata,
    } = values ?? {}
    const errorTitle = validateTitle(title, t)
    const errorDescription = validateDescription(description, t)
    const errorAcademicField = validateAcademicField(academic_field, t)
    const errorTimeOpen = validateTimeOpen(time_open, typeForm, t)
    const errorTimeClose = validateTimeClose(time_open, time_close, t)
    const errorAcademicYear = validateAcademicYear(academic_year, t)
    const errorLastEnrollmentTime = validateLastEnrollmentTime(
        time_open,
        last_enrollment_time,
        t
    )

    // ── validate metadata ──────────────────────────────────────────
    const metaItems = Array.isArray(metadata) ? metadata : []

    if (metaItems.length === 0) {
        // bỏ dòng này nếu metadata không bắt buộc phải có ít nhất 1 field
        // errors.metadata = t('required field')
    }

    const metaErrors = {}

    metaItems.forEach(function (item, index) {
        // title bắt buộc
        if (!item.title || !item.title.trim()) {
            metaErrors['[' + index + '].title'] = t('required field')
        } else if (item.title.trim().length > 120) {
            metaErrors['[' + index + '].title'] = t('title_max_120')
        }

        // description max 500
        if (item.description && item.description.trim().length > 500) {
            metaErrors['[' + index + '].description'] = t('description_max_500')
        }

        // options bắt buộc nếu là radioField / checkboxField
        if (['radioField', 'checkboxField'].includes(item.ctrl_type)) {
            var opts = Array.isArray(item.options) ? item.options : []
            if (opts.length === 0) {
                metaErrors['[' + index + '].options'] = t('options_min_1')
            } else {
                opts.forEach(function (opt, ii) {
                    if (!opt || !String(opt).trim()) {
                        metaErrors['[' + index + '].options[' + ii + ']'] =
                            t('required field')
                    } else if (String(opt).trim().length > 120) {
                        metaErrors['[' + index + '].options[' + ii + ']'] =
                            t('option_max_120')
                    }
                })
            }
        }
    })

    return {
        ...errorTitle,
        ...errorDescription,
        ...errorAcademicField,
        ...errorTimeOpen,
        ...errorTimeClose,
        ...errorAcademicYear,
        ...errorLastEnrollmentTime,
        // ...(Object.keys(metaErrors).length > 0
        //   ? { metadata: t('invalid data') }
        //   : {}),
    }
}

const StartDate = formSchema.started_finished_date.children[0].component
const EndDate = formSchema.started_finished_date.children[1].component
const AcademicYear = formSchema.started_finished_date.children[2].component

// Wraps create form body — reads curriculum_id to conditionally hide/show fields.
const CreateCourseFormContent = () => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const hasCurriculum = !!values.curriculum_id

    return (
        <div className="flex flex-col gap-3">

            <div className="flex gap-3 px-3 py-2 bg-blue-50 rounded-lg">
                <BsInfoCircle size={18} className="text-blue-400"/>
                <div className="flex flex-col flex-1 gap-1">
                    <span className="font-semibold text-blue-600">
                        {t('what is a course')}
                    </span>
                    <span className="text-color-200 italic tracking-wider">
                        {t('what is a course description')}
                    </span>
                </div>
            </div>

            {/* Chương trình đào tạo ĐƯA LÊN ĐẦU (trước cả Tên khóa học) - chọn chương trình quyết
                định các bước sau (có curriculum thì ẩn hẳn "Giới thiệu khóa học", hiện Preview kế
                thừa) nên hỏi trước khi đặt tên hợp lý hơn. Field này chỉ tự do đổi qua lại lúc TẠO
                (chưa lưu) - form Sửa (EditCourseModal/CurriculumField) vẫn cho CHỌN nếu khóa CHƯA
                từng gán chương trình, nhưng MỘT KHI đã lưu/gán rồi thì khoá lại vĩnh viễn (không cho
                đổi nữa) - đổi giữa chừng có thể lệch với subjects/curriculum đã snapshot vào khóa lúc
                tạo, khớp CourseController::editCourse ("curriculum_id is immutable after assignment"). */}
            <Pure>
                <Section title={t('training program')}>
                    <FieldsFactory formSchema={[formSchema.curriculum_id]}/>
                    <Field label={t('academic field')} hint={t('academic field hint')}>
                        <FieldsFactory formSchema={[formSchema.academic_field]}/>
                    </Field>
                </Section>
            </Pure>

            {/* Preview môn học của chương trình ĐẶT NGAY SAU khi chọn chương trình (thay vì tuốt dưới
                cùng, sau cả Tên khóa học/Thời gian như trước - bạn góp ý 2 khối này nên liền mạch vì
                cùng 1 luồng "chọn chương trình -> xem/chỉnh môn kế thừa"), TRƯỚC khi đặt tên khóa. */}
            {hasCurriculum && <CurriculumPreview/>}

            <Pure>
                <Section title={t('title of course')} theme="highlight">
                    <Field label={t('title of course')} required={formSchema.title.isRequired}>
                        <FieldsFactory formSchema={[formSchema.title]} />
                    </Field>
                </Section>
            </Pure>

            <Section title={t('time')}>
                <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                <FieldsFactory formSchema={[formSchema.registration_period_quota]}/>
                <p className="text-2xs text-color-400 italic leading-relaxed">
                    {t('student quota hint')}
                </p>
            </Section>

            {!hasCurriculum && (
                <Pure>
                    <Section title={t('about course')}>
                        <FieldsFactory formSchema={[formSchema.about]}/>
                    </Section>
                </Pure>
            )}
        </div>
    )
}

export const CreateCourseModal = ({
                                      owner_id,
                                      owner_type,
                                      onCancel = NullAsDefault,
                                      onSubmit = NullAsDefault,
                                  }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => ({
        owner_id,
        owner_type,
        title: undefined,
        description: undefined,
        curriculum_id: undefined,
        _curriculum: undefined,
    }), [owner_id, owner_type])

    return (
        <Formik
            validate={validate(t, "Create")}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    time_open,
                    time_close,
                    last_enrollment_time,
                    _curriculum,
                    ...rest
                } = values ?? {}
                const hasCurriculum = !!rest.curriculum_id
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            title: rest.title?.trim(),
                            academic_field: rest.academic_field?.trim(),
                            time_open: getDateString(time_open),
                            time_close: getDateString(time_close),
                            // about will be snapshotted from curriculum on backend when curriculum_id is set
                            about: (!hasCurriculum && about) ? convertToString(about) : undefined,
                            last_enrollment_time: getDateString(last_enrollment_time),
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                <ErrorSummaries labels={COURSE_ERROR_LABELS}/>
                <ActionModalWrapper action={<FormActionGroups submitProps={{label: 'create'}}/>}>
                    <CreateCourseFormContent/>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const EditCourseModal = ({
                                    owner_id,
                                    owner_type,
                                    onCancel = NullAsDefault,
                                    onSubmit = NullAsDefault,
                                    isSubmitting = false,
                                    initialValues = {},
                                }) => {
    const t = useTranslate()
    const [current, setCurrent] = useState('overview')

    // initialValues (thường là currentCourse lấy qua useSelectEntities ở CourseDetail) có thể bị
    // "nhấp nháy": các API list khác trên cùng màn (vd dropdown khóa ở CourseClassOperations)
    // normalize cùng entity 'courses' bằng transformer rút gọn, ghi đè bản đầy đủ từ
    // course_getCourseById_Api rồi lại bị ghi đè ngược -> initialValues đổi ref liên tục ->
    // Formik enableReinitialize reset mỗi render -> values.owner_id/curriculum_id chớp tắt ->
    // CurriculumField đổi nhánh -> CurriculaSelect (antd Select) mount/unmount liên tục ->
    // ResizeObserver "Maximum update depth exceeded". Chốt 1 bản ổn định theo id khóa; chỉ nhận
    // bản mới khi ĐỔI khóa hoặc bản mới đầy đủ hơn (có owner mà bản đang giữ chưa có).
    const stableInitRef = useRef(null)
    const incomingCourseId = getId(initialValues)
    const heldCourseId = getId(stableInitRef.current)
    if (
        incomingCourseId &&
        // đổi khóa -> nhận bản mới; hoặc bản đang giữ còn thiếu owner (chưa "đủ" cho form) thì
        // tiếp tục nhận cho tới khi có 1 bản đủ, rồi đóng băng để hết nhấp nháy.
        (heldCourseId !== incomingCourseId || !stableInitRef.current?.owner)
    ) {
        stableInitRef.current = initialValues
    }
    const stableInitialValues = stableInitRef.current || initialValues || {}

    // owner cũng chốt bằng ref: một khi đã biết thì không cho tụt về undefined giữa chừng, tránh
    // CurriculaSelect đổi nhánh có-fetch / rỗng.
    const ownerLatchRef = useRef({owner_id: undefined, owner_type: undefined})
    const candOwnerId = owner_id ?? getId(stableInitialValues?.owner)
    const candOwnerType = owner_type ?? getType(stableInitialValues?.owner)
    if (candOwnerId && candOwnerType && !ownerLatchRef.current.owner_id) {
        ownerLatchRef.current = {owner_id: candOwnerId, owner_type: candOwnerType}
    }
    const latchedOwnerId = ownerLatchRef.current.owner_id ?? candOwnerId
    const latchedOwnerType = ownerLatchRef.current.owner_type ?? candOwnerType

    const _initialValues = useMemo(() => {
        const {
            about,
            time_open,
            time_close,
            last_enrollment_time,
            student_quota,
            metadata = [],
            categories,
            ...rest
        } = stableInitialValues ?? {}
        const status_options = [
            course_status_options.normal,
            course_status_options.closed,
        ].map(deepTranslate(t))
        return {
            ...rest,
            // owner đã chốt bằng ref ở trên (latchedOwner*) - dùng cho CurriculaSelect. Thiếu 2
            // giá trị này thì CurriculaSelect gọi API với path :type/:id chưa thay -> BE trả 500.
            owner_id: latchedOwnerId,
            owner_type: latchedOwnerType,
            status_options,
            medal_id: rest?.medal?.id,
            curriculum_id: rest?.curriculum_id ?? null,
            about: convertFromString(about),
            time_open: parseDayjs(time_open),
            time_close: parseDayjs(time_close),
            last_enrollment_time: parseDayjs(last_enrollment_time),
            student_quota: student_quota ? Number(student_quota) : null,
            // Mặc định VND ngay từ initialValues (không chỉ fallback hiển thị ở Select) - tránh ca
            // user chỉ nhập fee_amount mà không đụng vào dropdown currency (thấy sẵn "VND" trên UI
            // tưởng đã chọn), submit thiếu fee_currency_code sẽ bị BE chặn (rule required_with).
            fee_currency_code: rest?.fee_currency_code || 'VND',
            metadata: metadata ?? [],
            categories: _.isArray(categories)
                ? categories.map((category) => category?.id)
                : [],
        }
    }, [stableInitialValues, latchedOwnerId, latchedOwnerType])

    return (
        <Formik
            validate={validate(t, "Edit")}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    time_open,
                    time_close,
                    status_options,
                    last_enrollment_time,
                    metadata,
                    categories,
                    linked_pages,
                    // curriculum: object đầy đủ CurriculumField set kèm curriculum_id để hiện lại badge
                    // ngay (không cần chờ refetch) - chỉ curriculum_id mới là field thật sự gửi lên BE
                    // (khớp $editable ở Course.php), curriculum KHÔNG nằm trong $editable nên loại khỏi
                    // payload, tránh gửi thừa object lồng nhau lên editCourse.
                    curriculum,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            title: rest.title?.trim(),
                            academic_field: rest.academic_field?.trim(),
                            metadata: JSON.stringify(metadata),
                            time_open: getDateString(time_open),
                            time_close: getDateString(time_close),
                            about: about ? convertToString(about) : undefined,
                            last_enrollment_time: getDateString(last_enrollment_time),
                            categories: categories
                                ? Array.from(categories).join(',')
                                : undefined,
                            linked_pages: linked_pages ? JSON.stringify(linked_pages) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                {/* Bật lại ErrorSummaries (trước đây bị comment) - component THẬT đã có sẵn ở
                    createEditableForm.js (đúng cái SubjectForm.js đang dùng), không phải tự chế mới.
                    Đọc trực tiếp errors qua FormikConsumer nên không lọc theo touched - hiện được cả
                    lỗi ở field user CHƯA ĐỤNG TỚI (vd academic_year tự suy ra khi đổi ngày bắt đầu,
                    có thể sai ngay từ lúc mở form do validateOnMount) - trước đây sửa field khác
                    không thấy báo gì vì lỗi thật nằm ở field này, ngoài tầm nhìn. */}
                <ErrorSummaries labels={COURSE_ERROR_LABELS}/>
                <SyncFormikSubmitting isSubmitting={isSubmitting}/>
                <ActionModalWrapper
                    tab={current}
                    action={<FormActionBar submitText="save"/>}
                    onTabChange={(key) => {
                        setCurrent(key)
                    }}
                    tabs={[
                        {
                            key: 'overview',
                            label: 'overview',
                            component: ({child}) => child,
                        },
                        {
                            key: 'metadata',
                            label: 'registration questions',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.metadata]}/>
                            ),
                        },
                        {
                            key: 'about',
                            label: 'about course',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.about]}/>
                            ),
                        },
                        {
                            key: 'medal',
                            label: 'certification',
                            component: () => (
                                <div className="flex flex-col gap-3">
                                    <div className="px-3 py-1 flex gap-3 bg-blue-50 border border-blue-400 rounded-lg ">
                                        <BsInfoCircle size={18} className="mt-0.5 text-blue-400"/>
                                        <div className="flex flex-col">
                                            <span className="font-medium text-blue-500 tracking-wide">
                                                {t('what is certification')}
                                            </span>
                                            <div className="font-normal text-color-200 italic tracking-wider">
                                                {t('explain certification')}
                                            </div>
                                        </div>
                                    </div>
                                    <FieldsFactory formSchema={[formSchema.medal_id]}/>
                                    <div className="space-x-1 text-sm italic">
                                        <span className="font-medium text-color-100">
                                            {`* ${t('note')} : `}
                                        </span>
                                        <span className="text-color-400">
                                            {t('note explain certification')}
                                        </span>
                                    </div>
                                </div>
                            ),
                        },
                    ]}>

                    <CurriculumField ownerId={latchedOwnerId} ownerType={latchedOwnerType}/>

                    <Pure>
                        <Section title={t('basic information')}>
                            <Field label={t('cover photo')}>
                                <FieldsFactory formSchema={[formSchema.cover_photo]}/>
                            </Field>
                            <Field label={t('status')}>
                                <FieldsFactory formSchema={[formSchema.status]}/>
                            </Field>
                            <Field label={`${t('title')} - ${t('url')}`}>
                                <FieldsFactory formSchema={[formSchema.title, formSchema.idname]}/>
                            </Field>
                            <Field label={t('course code')}>
                                <FieldsFactory formSchema={[formSchema.course_idcode]}/>
                                <p className="text-2xs text-color-400 italic leading-relaxed tracking-wide">
                                    {t('course code hint')}
                                </p>
                            </Field>
                            <Field label={t('categories')}>
                                <FieldsFactory formSchema={[formSchema.categories]}/>
                            </Field>
                            <Field label={t('academic field')} hint={t('academic field hint')}>
                                <FieldsFactory formSchema={[formSchema.academic_field]}/>
                            </Field>
                            <Field label={t('short description')}>
                                <FieldsFactory formSchema={[formSchema.description]}/>
                            </Field>
                        </Section>
                    </Pure>
                    <Pure>
                        <Section title={t('time')}>
                            <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                            <FieldsFactory formSchema={[formSchema.registration_period_quota]}/>
                            <p className="text-2xs text-color-400 italic leading-relaxed">
                                {t('student quota hint')}
                            </p>
                        </Section>
                    </Pure>

                    <Pure>
                        <Section title={t('lookup preview info')} subtitle={t('lookup preview info description')}>
                            <Field label={t('person in charge')}>
                                <FieldsFactory formSchema={[formSchema.coordinator_name]}/>
                            </Field>
                            <Field label={`${t('location')} - ${t('schedule')}`}>
                                <FieldsFactory formSchema={[formSchema.location, formSchema.schedule]}/>
                            </Field>
                            <Field label={t('teaching mode')}>
                                <FieldsFactory formSchema={[formSchema.delivery_mode]}/>
                            </Field>
                        </Section>
                    </Pure>

                    {/* Khai báo học phí TÁCH RIÊNG thành 1 block - trước đây nằm chung "Thông tin
                        hiển thị khi tra cứu" cùng phụ trách/vị trí/hình thức giảng dạy, bạn báo khó
                        tìm ra khi cần chỉnh học phí. Từ đây trở xuống có thể phình khá dài (chọn
                        Linh động theo định mức mở ra cả bảng FeeTypesEditor), tách riêng cũng đỡ
                        dồn cục vào block "Thông tin hiển thị khi tra cứu" phía trên. */}
                    <Pure>
                        <Section title={t('tuition fee section title')} subtitle={t('tuition fee section subtitle')}>
                            <Field label={t('fee mode')}>
                                <FieldsFactory formSchema={[formSchema.fee_mode]}/>
                            </Field>
                            <Field label={`${t('fee amount')} - ${t('currency')}`}>
                                <FeeAmountCurrencyField/>
                            </Field>
                        </Section>
                    </Pure>

                    <Pure>
                        <Section title={t('admission and graduation info')} subtitle={t('admission and graduation info description')}>
                            <Field label={t('eligibility requirements')}>
                                <FieldsFactory formSchema={[formSchema.eligibility_requirements]}/>
                            </Field>
                            <Field label={t('required documents')}>
                                <FieldsFactory formSchema={[formSchema.required_documents]}/>
                            </Field>
                            <Field label={t('graduation criteria')}>
                                <FieldsFactory formSchema={[formSchema.graduation_criteria]}/>
                            </Field>
                        </Section>
                    </Pure>

                    <Pure>
                        <Section title={t('external links')} subtitle={t('external links description')}>
                            <FieldsFactory formSchema={[formSchema.linked_pages]}/>
                        </Section>
                    </Pure>

                    <Pure>
                        <Section title={t('posting permission')}>
                            <FieldsFactory formSchema={[formSchema.channel_post_permission]}/>
                        </Section>
                    </Pure>

                    <Pure>
                        <Section title={t('privacy')}>
                            <FieldsFactory formSchema={[formSchema.privacy]}/>
                        </Section>
                    </Pure>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const CopyCourseModal = ({
                                    owner_id,
                                    owner_type,
                                    onCancel = NullAsDefault,
                                    onSubmit = NullAsDefault,
                                    initialValues = {},
                                }) => {
    const t = useTranslate()
    const oldTitle = _.get(initialValues, 'title')

    const _initialValues = useMemo(() => ({
        title: _.get(initialValues, 'title'),
        time_open: parseDayjs(_.get(initialValues, 'time_open')),
        time_close: parseDayjs(_.get(initialValues, 'time_close')),
        academic_year: _.get(initialValues, 'academic_year'),

        copy_content: contentOptionsCopy
            .filter(({disabled}) => !disabled)
            .map(({value}) => value),

    }), [initialValues])

    const validateCopy = (values) => {
        const {
            title,
            time_open,
            time_close,
            academic_year,
        } = values ?? {}

        // Nếu title đã hợp lệ thì kiểm tra thêm title phải khác title cũ
        let errorTitle = validateTitle(title, t)

        if (
            !errorTitle &&
            oldTitle?.trim() &&
            title?.trim() === oldTitle.trim()
        ) {
            errorTitle = {
                title: t('copy_course_title_must_be_different'),
            }
        }

        const errorTimeOpen = validateTimeOpen(time_open, undefined, t)
        const errorTimeClose = validateTimeClose(time_open, time_close, t)
        const errorAcademicYear = validateAcademicYear(academic_year, t)

        return {
            ...errorTitle,
            ...errorTimeOpen,
            ...errorTimeClose,
            ...errorAcademicYear,
        }
    }

    return (
        <Formik
            validate={validateCopy}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const {time_open, time_close, ...rest} = values
                onSubmit({
                    ...rest,
                    title: rest.title?.trim(),
                    time_open: getDateString(time_open),
                    time_close: getDateString(time_close),
                })
            }}>
            {({values}) => {
                const selectedContents = values?.copy_content || []
                return (
                    <React.Fragment>
                        {/* Note */}
                        <div className="mb-4 p-3 border border-blue-300 bg-blue-50 rounded-xl flex flex-col gap-3">
                            <div className="flex flex-col gap-0.5">
                                <span className="font-semibold text-blue-700">
                                    {`* ${t('note')}: `}
                                </span>
                                <span className="ml-3 text-blue-500 tracking-wider">
                                    {t('duplicate course description')}
                                </span>
                            </div>
                            <div className="flex flex-col gap-2">
                                {/* Header */}
                                <div className="grid grid-cols-3 gap-1">
                                <span className="text-center font-semibold text-xs text-slate-400 uppercase tracking-wide">
                                    {t('original')}
                                </span>
                                    <span/>
                                    <span className="text-center font-semibold text-xs text-slate-400 uppercase tracking-wide">
                                    {t('copy data')}
                                </span>
                                </div>
                                {/* Content status */}
                                <div className="flex flex-col gap-2">
                                    {contentOptionsCopy.map(({value, label}) => {
                                        const isSelected = selectedContents.includes(value)

                                        return (
                                            <div key={value} className="grid grid-cols-3 items-center gap-1">
                                                {/* Original */}
                                                <div className="px-2.5 py-2 text-center border border-color-100 background rounded-lg font-semibold text-sm tracking-wide">
                                                    {t(label)}
                                                </div>
                                                {/* Arrow */}
                                                <div className="flex justify-center">
                                                    <BsArrowRight
                                                        size={18}
                                                        className="text-gray-500"
                                                    />
                                                </div>
                                                {/* Status */}
                                                <div
                                                    className={classNames(
                                                        'px-2.5 py-2 text-center border rounded-lg text-xs font-semibold tracking-wide',
                                                        isSelected
                                                            ? 'bg-green-50 border-green-100 text-green-600'
                                                            : 'bg-orange-50 border-orange-100 text-orange-600'
                                                    )}>
                                                    {t(
                                                        isSelected
                                                            ? 'keep unchanged'
                                                            : 'set up again'
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Form content */}
                        <Section>
                            <Field label={t('title')}>
                                <FieldsFactory formSchema={[formSchema.title]}/>
                            </Field>
                            <Field label={`${t('time')} - ${t('academic year')}`}>
                                <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                            </Field>
                        </Section>
                        <div className="mt-3 flex flex-col gap-2">
                            <Field>
                                <FieldsFactory formSchema={[formSchema.copy_content]}/>
                            </Field>
                            <div className="text-gray-700 italic tracking-wider">
                                *{t(
                                    'not copy content student description',
                                    {note: ( <strong>{t('promote students')}</strong> )
                                })}
                            </div>
                        </div>
                        <FormActionBar submitText="duplicate"/>
                    </React.Fragment>
                )
            }}
        </Formik>
    )
}

export const PromoteStudentsWizard = ({
                                          owner_id,
                                          owner_type,
                                          course_id,
                                          isSubmitting = false,
                                          onCancel = NullAsDefault,
                                          onSubmit = NullAsDefault,
                                      }) => {
    const t = useTranslate()
    const [step, setStep] = useState(0)
    const [sourceCourseId, setSourceCourseId] = useState(null)
    const [classMappings, setClassMappings] = useState({})
    const [previewData, setPreviewData] = useState(null)
    const [isFetching, setIsFetching] = useState(false)

    const {response: coursesResponse, isLoading: coursesLoading} = useAsync({
        apiInfo: course_getCourses_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })
    const courses = _.get(coursesResponse, 'data.data', []).filter((c) => c.id !== course_id)

    const {handleAsyncAction: fetchPreview} = useAsyncAction({
        apiInfo: course_promotePreview_Api,
        onSuccess: (result) => {
            setPreviewData(result)
            setIsFetching(false)
        },
        onError: (error, errorObj) => {
            setIsFetching(false)
            notifyOnError(t)(error, errorObj)
        },
    })

    const sourceClasses = _.get(previewData, 'source_classes', [])
    const targetClasses = _.get(previewData, 'target_classes', [])

    const usedTargetIds = useMemo(
        () => Object.values(classMappings).filter(Boolean),
        [classMappings]
    )

    const mappedPairs = useMemo(
        () =>
            Object.entries(classMappings)
                .filter(([, tid]) => Boolean(tid))
                .map(([sid, tid]) => ({
                    sourceClass: sourceClasses.find((c) => c.id === sid),
                    targetClass: targetClasses.find((c) => c.id === tid),
                }))
                .filter(({sourceClass, targetClass}) => sourceClass && targetClass),
        [classMappings, sourceClasses, targetClasses]
    )

    const totalCanPromote = mappedPairs.reduce((s, {sourceClass}) => s + (sourceClass?.can_promote ?? 0), 0)
    const totalSkip = mappedPairs.reduce(
        (s, {sourceClass}) => s + (sourceClass?.already_in_target ?? 0) + (sourceClass?.not_completed ?? 0),
        0
    )

    const autoMap = useCallback(() => {
        const newMappings = {}
        sourceClasses.forEach((sc) => {
            const match = targetClasses.find(
                (tc) => tc.title.toLowerCase().trim() === sc.title.toLowerCase().trim()
            )
            newMappings[sc.id] = match ? match.id : null
        })
        setClassMappings(newMappings)
    }, [sourceClasses, targetClasses])

    const setMapping = useCallback((sourceId, targetId) => {
        setClassMappings((prev) => ({...prev, [sourceId]: targetId || null}))
    }, [])

    const goToStep1 = () => {
        setPreviewData(null)
        setClassMappings({})
        setIsFetching(true)
        fetchPreview({source_course_id: sourceCourseId}, {id: course_id})
        setStep(1)
    }

    const handleConfirm = () => {
        const class_mappings = JSON.stringify(
            mappedPairs.map(({sourceClass, targetClass}) => ({
                source_class_id: sourceClass.id,
                target_class_id: targetClass.id,
            }))
        )
        onSubmit({source_course_id: sourceCourseId, class_mappings})
    }

    const STEPS = [
        {title: t('source course')},
        {title: t('map classes')},
        {title: t('confirm')},
    ]

    return (
        <Spin spinning={isSubmitting} tip={t('processing')}>
            <div className="p-0 md:p-4 flex flex-col gap-6">

                <div className="p-3 border border-blue-300 bg-blue-50 rounded-xl text-xs space-x-1">
                    <span className="font-semibold text-blue-700 italic">
                        {`* ${t('note')}: `}
                    </span>
                    <span className="text-blue-500 tracking-wider">
                        {t('promote students modal description')}
                    </span>
                </div>

                <Steps current={step} items={STEPS} size="small"/>

                {/* Bước 0: Chọn khóa nguồn */}
                {step === 0 && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-sm text-gray-800 tracking-wide">
                                {t('source_course')}
                            </span>
                            <div style={{height: 3}} className="w-20 rounded-full bg-gray-400"/>
                            <span className="text-xs text-color-300 tracking-wider">
                                {t('source_course_description')}
                            </span>
                        </div>
                        <SelectWidget
                            loading={coursesLoading}
                            className="w-full"
                            placeholder={t('select source course')}
                            value={sourceCourseId || undefined}
                            allowClear
                            showSearch
                            optionLabelProp="label"
                            filterOption={(input, option) =>
                                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={(value) => {
                                setSourceCourseId(value || null)
                                setClassMappings({})
                                setPreviewData(null)
                            }}>
                            {courses.map((c) => (
                                <Select.Option key={c.id} value={c.id} label={c.title} searchText={c.title}>
                                    <div className="flex flex-col gap-0.5 py-0.5">
                                        <span className="font-semibold text-sm">{c.title}</span>
                                        {c.academic_year > 0 && (
                                            <span className="text-xs text-color-400">{c.academic_year}</span>
                                        )}
                                    </div>
                                </Select.Option>
                            ))}
                        </SelectWidget>
                        <div className="flex justify-end">
                            <Button type="primary" disabled={!sourceCourseId} onClick={goToStep1}>
                                {t('next')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Bước 1: Ghép lớp */}
                {step === 1 && (
                    <div className="flex flex-col gap-3">
                        {isFetching ? (
                            <div className="flex justify-center py-10">
                                <Spin size="default"/>
                            </div>
                        ) : sourceClasses.length === 0 ? (
                            <div className="text-center text-sm text-color-400 py-8">
                                {t('no classes in source course')}
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-4">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="font-bold text-sm text-gray-800 tracking-wide">
                                            {sourceClasses.length} {t('classes')} → {targetClasses.length} {t('classes')}
                                        </span>
                                        <div style={{height: 3}} className="w-20 rounded-full bg-gray-400"/>
                                        <span className="text-xs text-color-300 tracking-wider">
                                            {t('auto map description')}
                                        </span>
                                    </div>
                                    <Button
                                        color="primary" variant="filled"
                                        onClick={autoMap}>
                                        {t('auto map')}
                                    </Button>
                                </div>
                                <div className="rounded-lg border border-color-50 overflow-hidden">
                                    <div className="grid items-center gap-2 px-3 py-2 background-200 text-xs font-semibold text-color-300 uppercase tracking-wide"
                                        style={{gridTemplateColumns: '1fr 60px 20px 1fr'}}>
                                        <span>{t('source class')}</span>
                                        <span className="text-center whitespace-no-wrap">{t('students')}</span>
                                        <span/>
                                        <span className="pl-2">{t('target class')}</span>
                                    </div>
                                    {sourceClasses.map((sc, index) => (
                                        <div
                                            key={sc.id}
                                            className={classNames(
                                                'grid items-center gap-2 px-3 py-2 text-sm border-t border-color-50',
                                                index % 2 === 0 ? 'background' : 'background-50'
                                            )}
                                            style={{gridTemplateColumns: '1fr 60px 20px 1fr'}}>
                                            {/* Cột 1 */}
                                            <div className="flex flex-col min-w-0 pr-2">
                                                <span className="font-medium truncate">
                                                    {sc.title}
                                                </span>
                                                {sc.already_in_target > 0 && (
                                                    <span className="text-xs text-color-400">
                                                        {sc.already_in_target} {t('already in course')}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Cột 2 */}
                                            <span className="text-center font-mono text-color-300">
                                                {sc.student_count}
                                            </span>
                                            {/* Cột 3 */}
                                            <BsArrowRight size={18} className="mx-auto text-center text-gray-700" />
                                            {/* Cột 4 */}
                                            <SelectWidget
                                                size="small"
                                                className="ml-2 w-full"
                                                placeholder={t('skip')}
                                                value={classMappings[sc.id] || undefined}
                                                allowClear
                                                onChange={(val) => setMapping(sc.id, val)}>
                                                {targetClasses.map((tc) => (
                                                    <Select.Option
                                                        key={tc.id}
                                                        value={tc.id}
                                                        disabled={
                                                            usedTargetIds.includes(tc.id) &&
                                                            classMappings[sc.id] !== tc.id
                                                        }>
                                                        <span>{tc.title}</span>
                                                        {tc.student_count > 0 && (
                                                            <span className="ml-1 text-xs text-color-400">
                                                            ({tc.student_count})
                                                        </span>
                                                        )}
                                                    </Select.Option>
                                                ))}
                                            </SelectWidget>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="flex justify-between pt-1">
                            <Button
                                onClick={() => {
                                    setStep(0)
                                    setPreviewData(null)
                                    setClassMappings({})
                                }}>
                                {t('back')}
                            </Button>
                            <Button
                                type="primary"
                                disabled={mappedPairs.length === 0}
                                onClick={() => setStep(2)}>
                                {t('next')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Bước 2: Xác nhận */}
                {step === 2 && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-sm text-gray-800 tracking-wide">
                                {t('confirmation_promote')}
                            </span>
                            <div style={{height: 3}} className="w-20 rounded-full bg-gray-400"/>
                            <span className="text-xs text-color-300 tracking-wider">
                                {t('confirmation_promote_description')}
                            </span>
                        </div>

                        <div className="rounded-lg border border-color-50 overflow-hidden">
                            <div className="grid items-center gap-2 px-3 py-2 background-200 text-xs font-semibold text-color-300 uppercase tracking-wide"
                                style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                <span>{t('source class')}</span>
                                <span/>
                                <span>{t('target class')}</span>
                                <span className="text-center text-2xs text-green-600">{t('can promote')}</span>
                                <span className="text-center">{t('skip')}</span>
                            </div>
                            {mappedPairs.map(({sourceClass, targetClass}, index) => (
                                <div
                                    key={sourceClass.id}
                                    className={classNames(
                                        'grid items-center gap-2 px-3 py-2 text-sm border-t border-color-50',
                                        index % 2 === 0 ? 'background' : 'background-50'
                                    )}
                                    style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                    <span className="font-medium truncate pr-2">{sourceClass.title}</span>
                                    <BsArrowRight size={18} className="mx-auto text-center text-gray-700" />
                                    <span className="truncate pr-2">{targetClass.title}</span>
                                    <span className="text-center font-semibold text-green-600">
                                        {sourceClass.can_promote}
                                    </span>
                                    <span className="text-center text-color-400 text-xs">
                                        {(sourceClass.already_in_target ?? 0) + (sourceClass.not_completed ?? 0)}
                                    </span>
                                </div>
                            ))}
                            <div
                                className="grid items-center gap-2 px-3 py-2 border-t border-color-200 background-100 text-sm font-semibold"
                                style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                <span className="uppercase text-xs text-color-300 tracking-wide">{t('total')}</span>
                                <span/>
                                <span/>
                                <span className="text-center text-green-600">{totalCanPromote}</span>
                                <span className="text-center text-color-400 text-xs">{totalSkip}</span>
                            </div>
                        </div>

                        {totalCanPromote > 0 && (
                            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                                {totalCanPromote} {t('students will be promoted')}
                            </div>
                        )}

                        {totalCanPromote === 0 && (
                            <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-700">
                                <div>{t('no students eligible for transfer')}.</div>
                                <div>{t('please complete final assessment before retrying transfer')}.</div>
                            </div>
                        )}

                        <div className="flex justify-between">
                            <Button onClick={() => setStep(1)} disabled={isSubmitting}>{t('back')}</Button>
                            <Button
                                type="primary"
                                disabled={totalCanPromote === 0 || isSubmitting}
                                loading={isSubmitting}
                                onClick={handleConfirm}>
                                {t('promote students')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Spin>
    )
}

export const TutorialCourseModalContent = ({onCancel, onHandleCreate}) => {
    const t = useTranslate()

    const ContentBlock = ({
                              right = false,
                              style,
                              icon,
                              title,
                              content,
                              renderContent,
                              className,
                          }) => (
        <div
            style={style}
            className={classNames(
                'cursor-pointer hover:shadow-items-primary border border-primary-200 hover:border-primary-600 relative',
                className
            )}>
            <div className="flex flex-col">
                <div
                    style={{
                        borderTopLeftRadius: right === true ? 5 : 20,
                        borderTopRightRadius: 20,
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-primary-50 leading-tight">
                    {icon}
                    <span className="font-bold text-primary uppercase tracking-wider">
            {t(title)}
          </span>
                </div>
                {renderIfElse(
                    renderContent,
                    renderContent && renderContent(),
                    <div className="px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                        {t(content)}
                    </div>
                )}
                <div className="text-right mx-3 mb-1 text-2xs text-color-500 hover:text-color-100 italic">
                    {`${t('more')}  >>`}
                </div>
            </div>
        </div>
    )

    const PopupBlock = ({content, subTitle, example, className}) => (
        <div className={classNames('flex flex-col gap-1', className)}>
            <div className="text-xs text-color-200 italic">{t(content)}</div>
            <div className="text-sm text-color-500 italic leading-tight">
                {t(subTitle)}
            </div>
            <div className="ml-1">{example}</div>
        </div>
    )

    const ItemExample = ({title}) => (
        <span className="space-x-1">
      <GoDotFill
          size={10}
          className="text-color-500"
      />
      <span className="text-xs italic">{title}</span>
    </span>
    )

    const CustomPopover = ({popupContent, content}) => (
        <Popover
            classNames={{root: 'PopupContent'}}
            placement="left"
            mouseEnterDelay={0.3}
            destroyOnHidden
            trigger="click"
            content={popupContent}>
            {content}
        </Popover>
    )

    return (
        <div className="flex flex-col gap-4">
            {/*<ListHeaderWithLine title="Course Management Guide" line={false} className="text-xl"/>*/}
            <div className="grid items-center grid-cols-2 gap-6">
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            className="p-1"
                            content="course tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Khóa Huấn luyện Huynh trưởng cấp I - 2024"/>
                                    {/*<ItemExample title="Khóa Huấn luyện Huynh trưởng cấp II - 2024" />*/}
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                style={{borderRadius: '20px 20px 5px 20px'}}
                                icon={
                                    <PiChalkboardSimpleBold
                                        size={14}
                                        className="text-primary"
                                    />
                                }
                                title="course"
                                content="course tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                'A Course is a feature that provides information about content, objectives, and training opportunities, managing basic teaching content.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'The goal of creating a Course is to publish the teaching program (content, timing) and outcomes after the training process for students.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                "It allows learners to view information and directly register for the course through the 'Course Registration' function."
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
                <div className="flex flex-center">
                    <LuCornerRightDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-center">
                    <LuCornerLeftDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            content="classes tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Lớp 1 - 1"/>
                                    <ItemExample title="Lớp giáo lý dự tòng 1 ( buổi tối 2 4 6 )"/>
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                right={true}
                                style={{borderRadius: '5px 20px 20px 20px'}}
                                icon={
                                    <ClassroomSvg
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="classes"
                                content="classes tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                'Classes under the Course plan effective teaching through organized classes.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'A class is an administrative unit created to ensure effective organization of teaching.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'Add classes in the management section for each course. Class division is based on the need for organizing teaching, usually depending on the number of students, and timing according to groups of students.'
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <CustomPopover
                    popupContent={
                        <div className="flex flex-col">
                            <PopupBlock
                                content="attendance tutorial extra description"
                                subTitle="example"
                                example={
                                    <div className="flex flex-col">
                                        <ItemExample title="Bảng điểm danh 1"/>
                                        <ItemExample title="Bảng điểm danh 2"/>
                                    </div>
                                }
                            />
                            <div className="border-b my-1"/>
                            <PopupBlock
                                content="grade tutorial extra description"
                                subTitle="example"
                                example={
                                    <div className="flex flex-col">
                                        <ItemExample title="Bảng điểm 1"/>
                                        <ItemExample title="Bảng điểm 2"/>
                                    </div>
                                }
                            />
                        </div>
                    }
                    content={
                        <div>
                            <ContentBlock
                                style={{borderRadius: '20px 20px 5px 20px'}}
                                icon={
                                    <TbTimelineEventText
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="activities during the training process"
                                renderContent={() => (
                                    <div className="flex flex-col gap-6 flex-1 px-3 py-2 my-4">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                "Monitoring the training process: attendance and students' score results."
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                            size={10}
                            className="text-color-000 mr-1"
                        />
                          {`${t('attendances')} : `}
                      </span>
                                            <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                            'Record participation through the attendance function in the class management section.'
                        )}
                      </span>
                                        </div>
                                        <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                            size={10}
                            className="text-color-000 mr-1"
                        />
                          {`${t('grades')} : `}
                      </span>
                                            <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                            'Record training results through the results of exams organized during the course.'
                        )}
                      </span>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
                <div className="flex flex-center">
                    <LuCornerRightDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-center">
                    <LuCornerLeftDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            className="p-1"
                            content="score summary tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Lớp học -> Học viên -> Đánh giá cuối khóa"/>
                                    <ItemExample
                                        title="Workspace -> Khóa học -> QL thông tin dưới khóa -> Lớp học -> Học viên -> Đánh giá cuối khóa"/>
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                right={true}
                                style={{borderRadius: '5px 20px 20px 20px'}}
                                icon={
                                    <TbSum
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="End-of-Course Evaluation"
                                content="score summary tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t('Synthesize final results and assess attendance.')}
                                        </div>
                                        <div>
                                            {t(
                                                'Access the class management section, evaluate the end of the course for each student to view and record results (including attendance assessment and exam results, helping to comprehensively assess performance).'
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
            </div>

            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-col flex-center gap-2 py-10">
                    <Button
                        type="primary"
                        className="rounded-lg no-border"
                        onClick={() => onHandleCreate && onHandleCreate()}>
                        {t('create course')}
                    </Button>
                    <span className="text-sm text-color-500 italic">
            {`* ${t('click here to create a new course')}`}
          </span>
                </div>
            </div>
        </div>
    )
}
